import { useState, useMemo } from "react";
import styled from "@emotion/styled";
import { Card, IconButton, Tooltip, lighten, darken } from "@mui/material";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik, Form } from "formik";
import { string, object, date, boolean } from "yup";
import { DateTime } from "luxon";

import { BasicFlex, ScrollableContainer } from "../../../utils/Common";
import NoItems from "../../../utils/NoItems";
import MultiStatusChip from "../../../utils/utilities/MultiStatusChip";
import FullDialog from "../../../utils/utilities/Dialog";
import SearchInput from "../../../utils/utilities/SearchInput";
import { formatPhoneNumber } from "../../../utils/util";

import { CheckoutContext, useCheckoutContext } from "../../../contexts/Checkout";
import { FormDataContext } from "../../../contexts/FormData";
import EditCheckout from "../../Checkout/EditCheckout";
import Pickup from '../../Layout/Components/Pickup';

import { schedulePickup, getScheduledPickups, getDailyPickups, deleteServiceTicket } from "../../Service_Tickets/apiService";
import useSimpleTable from "../../../hooks/useSimpleTable";
import useSnackbarStore from "../../../store/snackbar";

/* ---------- Layout ---------- */

const Pickups = styled.div`
  margin-top: 1.5rem;
  width: 30rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const PickupHeader = styled.div`
  height: 6.4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.4rem;
  letter-spacing: .4px;
  text-transform: uppercase;
  background-color: ${props => props.theme.colors.ribbon};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: .8rem;
  padding: 1rem .5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  border-left: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.white};
`;

const PickupList = styled.div`
  flex-grow: 1;
  /* min-height: 0;
  overflow-y: auto; */
  border: none;
  border-left: 1px solid ${props => props.theme.colors.border};
  border-radius: 0 0 5px 5px;
  padding: .8rem .5rem;
  background-color: ${props => props.theme.colors.white};
`;

const NoItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${props => props.theme.colors.border};
  padding: .6rem;
  border-radius: 8px;
  margin: .5rem 0;
`;

/* ---------- Card ---------- */

const PickupCard = styled(Card)`
  width: 100%;
  padding: 1rem 1.2rem;
  border-left-width: 4px;
  border-left-color: ${props => props.$type !== "service" ? props.theme.colors.primary : props.theme.colors.secondary};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .8rem;
`;

const CustName = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CustNumber = styled.div`
  font-size: 1.2rem;
  margin: .1rem 0 .8rem;
  color: ${props => props.theme.colors.mildText};
`;

const ServiceChip = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  padding: .3rem .8rem;
  border-radius: 5px;
  background-color: ${props => lighten(props.theme.colors.secondary, .87)};
  color: ${props => darken(props.theme.colors.secondary, .4)};
`;

const WigPill = styled.div`
  font-size: 1.1rem;
  font-family: monospace;
  padding: .3rem .6rem;
  border-radius: 5px;
  background-color: ${props => props.theme.colors.softBg};
  color: ${props => props.theme.colors.mildText};
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .8rem;
  margin-top: .8rem;
`;

const StylistRow = styled.div`
  display: flex;
  align-items: center;
  gap: .8rem;
  min-width: 0;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  strong {
    font-weight: 500;
    color: ${props => props.theme.colors.text};
  }
`;

const Avatar = styled.div`
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  background-color: ${props => lighten(props.theme.colors.primary, .87)};
  color: ${props => darken(props.theme.colors.primary, .4)};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: .6rem;
`

const CheckoutButton = styled(IconButton)`
  flex-shrink: 0;
  margin-left: auto;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.secondary};
`;

const DeleteButton = styled(IconButton)`
  flex-shrink: 0;
  margin-left: auto;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.error};
`;

const DeleteWarning = styled.div`
  margin: 0 1.6rem;
  font-size: 1.6rem;
`

const getInitials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join("");

const PickupItem = ({ pickup }) => {
  const { setService } = useCheckoutContext();

  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: () => deleteServiceTicket(pickup?.service_ticket_id),
    onSuccess: () => {
      queryClient.invalidateQueries(["pickups"]);
      showSnackbar("Successfully deleted!", "success");
    },
    onError: () => {
      showSnackbar("Something went wrong!", "error");
    },
  });

  const {
    client_name,
    customer_phone,
    status,
    service_name,
    wig_id,
    stylist,
    pickup_type = "service",
  } = pickup;

  return (
    <PickupCard variant="outlined" $type={pickup_type}>
      <NameRow>
        <CustName title={client_name}>{client_name}</CustName>
        <div style={{ flexShrink: 0 }}>
          <MultiStatusChip value={status} fs="1.2rem" px=".6rem" py=".2rem" values={[
            { label: "Awaiting Dropoff", value: "scheduled", status: "primary" },
            { label: "Prepping", value: "prepping", status: "warning" },
            { label: "In Progress", value: "in_progress", status: "warning" },
            { label: "Prep Done", value: "prep_done", status: "info" },
            { label: "Checked In", value: "checked_in", status: "secondary" },
            { label: "Completed", value: "completed", status: "success" },
          ]} />
        </div>
      </NameRow>
      <CustNumber>{formatPhoneNumber(customer_phone)}</CustNumber>

      <BasicFlex gap=".6rem" justify="flex-start" style={{ flexWrap: "wrap" }}>
        <ServiceChip>{service_name}</ServiceChip>
        {wig_id && <WigPill>{wig_id}</WigPill>}
      </BasicFlex>

      <BottomRow>
        {stylist && (
          <StylistRow>
            <Avatar>{getInitials(stylist)}</Avatar>
            <span>with <strong>{stylist}</strong></span>
          </StylistRow>
        )}
        <ActionButtons>
          {!['completed', 'cancelled', 'no_show'].includes(status) ? <Tooltip title="Complete checkout">
            <CheckoutButton color="secondary" size="small" aria-label="Complete checkout" onClick={() => setService(pickup)}>
              <ShoppingCartCheckoutRoundedIcon fontSize="small" />
            </CheckoutButton>
          </Tooltip> : null}
          <FullDialog
            title="Confirm Delete?"
            toggle={<Tooltip title="Delete pickup">
            <DeleteButton color="error" size="small" aria-label="Complete checkout">
              <DeleteRoundedIcon fontSize="small" />
            </DeleteButton>
            </Tooltip>}
            saveFunc={() => mutate()}
          >
           <DeleteWarning>Are you sure you want to delete this pickup? This action cannot be undone. Any attached invoices will be deleted as well.</DeleteWarning>
          </FullDialog>
          
        </ActionButtons>
      </BottomRow>
    </PickupCard>
  );
};

/* ---------- Main ---------- */

const toDateTime = (d) => {
  if (!d) return DateTime.now();
  if (DateTime.isDateTime(d)) return d;
  if (d instanceof Date) return DateTime.fromJSDate(d);
  const parsed = DateTime.fromISO(String(d));
  return parsed.isValid ? parsed : DateTime.now();
};

const getInitialValues = (date) => ({
  customer_id: "",
  service_name: "",
  stylist_id: "",
  wig_id: "",
  is_checked_in: true,
  scheduled_pickup_date: toDateTime(date),
});

const schema = object().shape({
  customer_id: string(),
  service_name: string(),
  stylist_id: string(),
  wig_id: string(),
  is_checked_in: boolean(),
  scheduled_pickup_date: date(),
});

const PickupComponent = ({ date }) => {
  const [service, setService] = useState(null);
  const [formValues, setFormValues] = useState({
    inventory_id: "",
    service_price: 0,
    total: 0,
    client_name: "",
  });

  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const initialValues = useMemo(() => getInitialValues(date), [date]);

  const { data } = useQuery({
    queryKey: ["pickups", "ready", date],
    queryFn: () => getDailyPickups(date),
    // enabled: !!date,
  });

  const pickups = data?.rows || [];

  const { data: pickup_list, setSearchTerm } = useSimpleTable({
    localData: pickups,
    rowsPerPage: 200,
    searchFields: ["client_name", "customer_phone"],
    defaultSort: "scheduled_pickup_date",
  });

  const { mutate } = useMutation({
    mutationFn: schedulePickup,
    onSuccess: () => {
      queryClient.invalidateQueries(["pickups"]);
      showSnackbar("Scheduled successfully!", "success");
    },
    onError: () => {
      showSnackbar("Something went wrong!", "error");
    },
  });

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    const dataPacket = { ...values, ...formValues };
    mutate({ dataPacket }, {
      onSuccess: () => resetForm(),
      onSettled: () => setSubmitting(false),
    });
  };

  return (
    <CheckoutContext.Provider value={{ service, setService }}>
      <Pickups>
        <PickupHeader>Pickups For Today</PickupHeader>

        <Toolbar>
          <FormDataContext.Provider value={{ formValues, setFormValues }}>
            <Formik
              initialValues={initialValues}
              validationSchema={schema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {() => (
                <Form>
                  <Pickup width="29rem" margin="0" />
                </Form>
              )}
            </Formik>
          </FormDataContext.Provider>
          <SearchInput
            onChange={(e) => setSearchTerm(e.target.value)}
            fieldsToSearch={["Customer", "Phone"]}
          />
        </Toolbar>

        <PickupList>
          {pickup_list?.rows?.length ? (
            <BasicFlex dir="column" gap="1rem">
              {pickup_list.rows.map(pickup => (
                <PickupItem key={pickup.service_ticket_id} pickup={pickup} />
              ))}
            </BasicFlex>
          ) : (
            <NoItemsContainer>
              <NoItems title="No pickups scheduled for today" />
            </NoItemsContainer>
          )}
        </PickupList>
      </Pickups>

      <FullDialog
        fullScreen
        forceOpen={!!service?.service_ticket_id}
        title="Service Checkout"
        useForm={true}
        forceClose={() => setService(null)}
      >
        <EditCheckout invalidateQueries={[["pickups", "ready"]]} />
      </FullDialog>
    </CheckoutContext.Provider>
  );
};

export default PickupComponent;